import { FileUp } from 'lucide-react';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';

export interface FileSelectorProps {
  file: File | null;
  setFile: (file: File | null) => void;
  onNext: () => void;
}

export function FileSelector({ file, setFile, onNext }: FileSelectorProps) {
  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      const file = acceptedFiles[0];
      if (file) setFile(file);
    },
    [setFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      //   '.xlsx',
      // ],
      // 'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  return (
    <Card className='mx-auto w-full max-w-4xl'>
      <CardContent className='p-6'>
        <h1 className='mb-6 text-2xl font-semibold'>Bulk Upload Classes</h1>
        <div className='space-y-6'>
          <ol className='list-inside list-decimal space-y-2'>
            <li>Download the template file (last updated on 01/30/2025).</li>
            <li>
              Fill in all required class details in the downloaded file. Ensure
              each field aligns with the format and naming in the template.
            </li>
            <li>Save the file as .xlsx, .xls, or .csv format.</li>
            <li>
              Upload the file below. New classes will be scheduled from the
              start date specified in the file. Use the Custom Date option in
              the file for specific start dates.
            </li>
            <li className='font-semibold'>
              Tuition Billing Cycle should be in the format (Monthly, One Time,
              Weekly, Bi-Weekly, Every X Days). When using &quot;Every X
              Days&quot;, the &quot;Billing Frequency Days&quot; column is
              required.
            </li>
            <li>
              Session, Group, and Location values must be copied and pasted for
              each row. Do not drag the fill handle, because spreadsheet apps
              can auto-increment numbers and create unintended duplicate
              records.
            </li>
          </ol>
          <div className='space-y-4 rounded-lg border p-4'>
            <h2 className='font-semibold'>Template Instructions</h2>
            <ol className='list-inside list-decimal space-y-2'>
              <li>
                The template includes fields such as Title, Class Color, Hide
                Class Status, Location, Room, Start Age (Year and Month), End
                Age (Year and Month), Instructor, Session, Start Date, End Date,
                Registration Start Date, Days of the Week, Start Time, End Time,
                Registration Fee, Tuition Fee, Costume Fee, Class Size, Group,
                and Tuition Billing Cycle.
              </li>
              <li>Do not add or remove columns from the template.</li>
              <li>
                Leave fields blank if no data is available for that cell. Do not
                use &quot;-&quot;, &quot;NA,&quot; or similar placeholders.
              </li>
              <li>
                Copy/paste the exact Session, Group, and Location value for each
                row. Dragging a cell&apos;s fill handle will auto-increment
                numbers and create duplicate records.
              </li>
            </ol>
          </div>
          <div className='space-y-6'>
            <div
              {...getRootProps()}
              className={cn(
                'cursor-pointer rounded-lg border-2 border-dashed border-primary p-6 text-center transition-colors',
                { 'bg-accent': isDragActive },
              )}
            >
              <input {...getInputProps()} />
              <FileUp className='mx-auto mb-4 size-12 text-primary' />
              {file ? (
                <p className='font-semibold'>File selected: {file.name}</p>
              ) : (
                <p>Drag & drop your file here, or click to select a file</p>
              )}
              <p className='mt-2 text-sm'>Accepted file types: .csv</p>
            </div>
            <div className='text-sm'>
              <Button asChild variant={'link'} className='px-0'>
                <a
                  href='https://storage.googleapis.com/enrolliocsv-example/csv%20v6.csv'
                  download='class-csv-example.csv'
                >
                  Download
                </a>
              </Button>{': '}
              Sample CSV
            </div>
            <div>
              <p>
                Need help! Chat in with live chat support or email support at{' '}
                <a href='mailto:support@enrollio.ai' className='underline'>
                  support@enrollio.ai
                </a>
                .
              </p>
            </div>
            <div className='flex flex-col'>
              <Button onClick={onNext} disabled={!file}>
                Go To Mapping
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
